'use client';
import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Users, BarChart, Brain, CheckCircle, LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import Link from 'next/link';
import { usePageView } from '@/lib/hooks/usePageView';

interface Step {
  icon: LucideIcon;
  title: string;
  description: string;
  points: string[];
}

const steps: Step[] = [
  {
    icon: FileText,
    title: 'Connect Your Supply Chain',
    description:
      'Integrate your shipping partners and supply chain data into one centralized platform.',
    points: [
      'EDI or API integration with carriers',
      'Custom data mapping tools',
      'Real-time data synchronization',
      'Partner onboarding support',
      'Automated data validation',
      'Secure connection setup'
    ]
  },
  {
    icon: Users,
    title: 'Monitor & Track',
    description:
      'Get real-time visibility into your shipments, inventory, and supply chain operations.',
    points: [
      'Live shipment tracking',
      'Inventory level monitoring',
      'Custom alert settings',
      'Status notifications',
      'Document management',
      'Mobile accessibility'
    ]
  },
  {
    icon: Brain,
    title: 'Analyze & Optimize',
    description:
      'Leverage our smart tools to optimize costs and improve efficiency.',
    points: [
      'Multi-modal shipping',
      'Landed cost calculation',
      'Smart quotation system',
      'Market rate analysis',
      'Trade compliance tools',
      'Cost benchmarking'
    ]
  },
  {
    icon: BarChart,
    title: 'Make Data-Driven Decisions',
    description:
      'Transform your supply chain data into actionable insights.',
    points: [
      'Performance Analytics',
      'Custom Reporting',
      'Trend Analysis',
      'Predictive Insights',
      'Risk Assessment',
      'ROI Tracking'
    ]
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};

interface StepCardProps {
  step: Step;
  index: number;
  totalSteps: number;
}

const StepCard = ({ step, index, totalSteps }: StepCardProps) => {
  return (
    <motion.div variants={itemVariants}
whileHover={{ scale: 1.02 }}
transition={{ duration: 0.2 }}>
      <Card className="relative overflow-hidden hover:shadow-lg transition-shadow bg-slate-900 border border-slate-800">
        {/* Connection Line */}
        {index < totalSteps - 1 && (
          <div className="absolute h-full w-0.5 bg-brand-subtle top-0 left-12 -bottom-16 z-0" />
        )}
 
        <CardContent className="p-8">
          <div className="flex items-start gap-8">
            {/* Step Number and Icon */}
            <motion.div
              className="relative z-10"
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.5 }}
            >
              <div className="rounded-full bg-brand-light p-4">
                <step.icon className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -top-4 -right-4 w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-bold">
                {index + 1}
              </div>
            </motion.div>
 
            {/* Content */}
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-4">{step.title}</h3>
              <p className="text-white/80 mb-6">{step.description}</p>
 
              <div className="grid sm:grid-cols-2 gap-4">
                {step.points.map((point, pIndex) => (
                  <motion.div
                    key={pIndex}
                    className="flex items-center gap-2 bg-black/20 p-3 rounded-lg backdrop-blur-sm"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: pIndex * 0.1 + index * 0.3 }}
                    whileHover={{ x: 10 }}
                  >
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-white/90">{point}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
 };

export default function LearnMore() {
  usePageView();
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
          How SupplySense 360 Works
        </h1>
        <p className="text-xl text-grey-600  max-w-2xl mx-auto">
          Our intelligent supply chain platform combines real-time tracking with advanced analytics to give you complete control over your global shipments.
        </p>
      </motion.div>

      {/* Steps */}
      <motion.div
        className="max-w-5xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {steps.map((step, index) => (
          <div key={index}
className="mb-16">
            <StepCard step={step}
index={index}
totalSteps={steps.length} />
          </div>
        ))}
      </motion.div>

      {/* CTA Section */}
      <motion.div
        className="text-center bg-slate-900 rounded-2xl p-8 md:p-12 mt-8 border border-slate-800"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        >
        <h2 className="text-2xl md:text-3xl font-bold mb-4 text-white">
          Ready to Transform Your Supply Chain?
        </h2>
        <p className="text-white/80 mb-8 max-w-2xl mx-auto">
          Join industry leaders using our platform to track shipments across air, ocean, rail, road, and parcel networks. Get real-time visibility from PO to delivery.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <motion.div whileHover={{ scale: 1.05 }}>
            <Button size="lg"
className="bg-brand-pbtn text-white">
              <Link href={`/dashboard/${process.env.NEXT_PUBLIC_DEMO_VULCAN_ID}`}>See It In Action</Link>
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }}>
            <Button variant="outline"
size="lg"
className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Link href="/contact">Schedule Demo</Link>
            </Button>
          </motion.div>
        </div>
        </motion.div>
    </div>
  );
}
